/**
 * 🏷️ 필드 기본 설명(Description) 자동 생성 유틸리티
 * 
 * 백엔드 dtoMappings.js의 label + formatHint를 프론트엔드에서 사용할 수 있도록
 * 경량 매핑 제공. 템플릿 필드 생성/수정 시 description 자동 채우기에 사용.
 */

const FIELD_DEFAULTS = {
  // --- 헤더 (단일) 필드 ---
  invoice_number:   { label: '송장 번호', formatHint: '영숫자 혼합. 숫자만 구성 시 O→0, l/I→1 보정. CCN/계약번호와 혼동 금지' },
  invoice_date:     { label: '송장 일자', formatHint: '날짜 형식. YYYY-MM-DD 또는 원본 형식 유지. O→0, l/I→1 보정 (예: 2O24→2024)' },
  bl_number:        { label: '선하증권 번호', formatHint: '영숫자 혼합 (예: MOLU12345678). O↔0, l↔1, S↔5, B↔8 보정' },
  seller_name:      { label: '수출자 (Shipper)', formatHint: '회사명/이름. rn→m, cl→d 혼동 주의. 영문 텍스트 그대로 추출' },
  buyer_name:       { label: '수입자 (Consignee)', formatHint: '회사명/이름. rn→m, cl→d 혼동 주의. 영문 텍스트 그대로 추출' },
  gross_weight:     { label: '총중량', formatHint: '순수 숫자 (소수점 포함). O→0, l/I→1, S→5, B→8 보정. 단위(KG 등) 제외' },
  net_weight:       { label: '순중량', formatHint: '순수 숫자 (소수점 포함). O→0, l/I→1, S→5, B→8 보정. 단위(KG 등) 제외' },
  package_count:    { label: '포장개수', formatHint: '정수. O→0, l/I→1, S→5, B→8 보정 필수. 수량(qty) 합계와 혼동 금지' },
  order_no:         { label: '주문번호', formatHint: '영숫자 혼합. 패턴 확인 후 O↔0, l↔1, S↔5 보정' },
  port_of_loading:  { label: '선적항', formatHint: '항구명 텍스트. 0→O, 8→B, 5→S, rn→m 보정 (예: T0KY0→TOKYO, 8USAN→BUSAN)' },
  port_of_discharge:{ label: '양륙항', formatHint: '항구명 텍스트. 0→O, 8→B, 5→S, rn→m 보정 (예: INCHE0N→INCHEON)' },
  vessel:           { label: '선박/항공편', formatHint: '선박명/항공편명. 영문 텍스트 그대로 추출' },
  marks_numbers:    { label: '화인 (Marks)', formatHint: '영숫자 혼합. 원문 그대로 추출' },
  payment_terms:    { label: '결제조건', formatHint: '결제조건 텍스트 (T/T, L/C, D/P 등)' },
  currency:         { label: '통화', formatHint: 'ISO 4217 통화코드 3자리 (USD, EUR, JPY, KRW). 5→S, 0→O 보정 (예: U5D→USD)' },
  incoterms:        { label: '인코텀즈', formatHint: '표준 인코텀즈 약어 (FOB, CIF, EXW, DDP 등). 0→O, 8→B 보정 (예: F0B→FOB, FO8→FOB)' },

  // --- 테이블 컬럼 필드 ---
  partno:           { label: '제품 코드', formatHint: '영숫자 혼합 코드. 동일 패턴의 다른 행 참고. O↔0, l↔1, S↔5, B↔8 보정' },
  part_number:      { label: '제품 코드', formatHint: '영숫자 혼합 코드. 동일 패턴의 다른 행 참고. O↔0, l↔1, S↔5, B↔8 보정' },
  description:      { label: '상품 설명', formatHint: '상품 설명 텍스트. rn→m, cl→d 혼동 주의. 원문 그대로 추출' },
  composition:      { label: '상품 성분', formatHint: '성분/재질 텍스트. 원문 그대로 추출' },
  hs_code:          { label: 'HS 코드', formatHint: '순수 숫자만 6~10자리. 점(.) 제거 (예: 8542.33.9000→8542339000). O→0, l/I→1, S→5, B→8, G→6 보정' },
  hscode:           { label: 'HS 코드', formatHint: '순수 숫자만 6~10자리. 점(.) 제거 (예: 8542.33.9000→8542339000). O→0, l/I→1, S→5, B→8, G→6 보정' },
  quantity:         { label: '단위 수량', formatHint: '순수 숫자. O→0, l/I→1, S→5, B→8 보정. 통화기호/단위 제외' },
  unit_type:        { label: '단위 유형', formatHint: '단위 약어 (SET, PCS, BOX, PKG, EA, KG 등)' },
  unit_price:       { label: '단가', formatHint: '순수 숫자 (소수점 포함). O→0, l/I→1, S→5, B→8, $→제외. 통화기호 제외' },
  amount:           { label: '금액', formatHint: '순수 숫자 (소수점 포함). O→0, l/I→1, S→5, B→8, $→제외. qty×unit_price 검증 권장' },
  origin:           { label: '원산지', formatHint: '국가코드 2자리 (CN, JP, US, KR) 또는 국가명. 0→O, 5→S, 8→B 보정 (예: C0→CO, U5→US)' },
  coo:              { label: '원산지', formatHint: '국가코드 2자리 (CN, JP, US, KR) 또는 국가명. 0→O, 5→S, 8→B 보정 (예: C0→CO, U5→US)' },
};

/**
 * 필드 키에 대한 기본 description 반환
 * @param {string} fieldKey - 필드 키 (예: "hs_code", "invoice_number")
 * @returns {string} - 기본 description 문자열 또는 빈 문자열
 */
export function getDefaultDescription(fieldKey) {
  if (!fieldKey) return '';
  const normalizedKey = fieldKey.toLowerCase().replace(/[\s-]/g, '_');
  const entry = FIELD_DEFAULTS[normalizedKey];
  if (!entry) return '';
  return `${entry.label}. ${entry.formatHint}`;
}

/**
 * 필드 키에 대한 기본 label만 반환 (짧은 설명)
 * @param {string} fieldKey
 * @returns {string}
 */
export function getDefaultLabel(fieldKey) {
  if (!fieldKey) return '';
  const normalizedKey = fieldKey.toLowerCase().replace(/[\s-]/g, '_');
  const entry = FIELD_DEFAULTS[normalizedKey];
  return entry?.label || '';
}

/**
 * 필드 키에 대한 formatHint만 반환
 * @param {string} fieldKey
 * @returns {string}
 */
export function getFormatHint(fieldKey) {
  if (!fieldKey) return '';
  const normalizedKey = fieldKey.toLowerCase().replace(/[\s-]/g, '_');
  const entry = FIELD_DEFAULTS[normalizedKey];
  return entry?.formatHint || '';
}

export default FIELD_DEFAULTS;
